import { useQuery } from '@apollo/client'
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react'

import { DataGridPremium } from '@mui/x-data-grid-premium'
import { Pagination, PaginationItem, SelectChangeEvent, Select, Typography } from '@mui/material'
import { Grid, MenuItem } from '@vapor/material'
import { GraphQLDataGridProps } from './graphql-data-grid.types'

const DEFAULT_PAGE_SIZE = 10

export type GraphQLDataGridRefProps<QueryData extends object, QueryRecordType extends object, QueryVariables extends object> = GraphQLDataGridProps<QueryData, QueryRecordType, QueryVariables>

type PropertyKey = string | number | symbol

export type QueryDataT<QueryDataType = Record<PropertyKey, any>> = QueryDataType

export type QueryRecordTypeT<QueryRecordTypeType = Record<PropertyKey, any>> = QueryRecordTypeType

export type QueryVariablesT<QueryVariablesType = Record<PropertyKey, any>> = QueryVariablesType

interface GraphQLDataGridType extends React.FC<GraphQLDataGridRefProps<QueryDataT, QueryRecordTypeT, QueryVariablesT>> {
  <D extends QueryDataT, R extends QueryRecordTypeT, V extends QueryVariablesT>(props: GraphQLDataGridRefProps<D, R, V>): ReturnType<React.FC<GraphQLDataGridRefProps<D, R, V>>>
}

const GraphQLDataGrid: GraphQLDataGridType = forwardRef((props, ref) => {
  const {
    query: queryDef,
    variables: getVariables,
    data: getDataFromResponse,
    pagination = false,
    pageSize: defaultPageSize = DEFAULT_PAGE_SIZE,
    labelRowsPerPage = 'Rows per page',
    rowsPerPageOptions,
    loading: externalLoading,
    ...datagridProps
  } = props

  const [pageSize, setPageSize] = useState<number>(defaultPageSize)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [hasMore, setHasMore] = useState<boolean | undefined>()
  const [currentRecords, setCurrentRecords] = useState<QueryRecordTypeT[]>([])

  const [loading, setLoading] = useState<boolean>(true)

  const buildVariables = useCallback(() => {
    if (getVariables) {
      let paginationForVariables
      if (pagination) {
        const oneMoreRecord = pagination === 'standard'
        paginationForVariables = { skip: (currentPage - 1) * (pageSize ?? 0), limit: pageSize + (oneMoreRecord ? 1 : 0) }
      }
      return getVariables({
        pagination: paginationForVariables,
      })
    }
    return undefined
  }, [pagination, pageSize, currentPage, getVariables])

  const { data: queryResult, loading: loadingQuery } = useQuery<QueryDataT, QueryVariablesT>(queryDef, {
    skip: false,
    variables: buildVariables(),
    ...props.options,
  })

  useImperativeHandle(ref, () => ({
    resetPagination() {
      setCurrentPage(1)
    },
  }))

  useEffect(() => {
    setLoading(loadingQuery || externalLoading === true)
  }, [externalLoading, loadingQuery])

  useEffect(() => {
    if (!loading && queryResult) {
      const responseRecords = getDataFromResponse(queryResult)
      if (pagination) {
        const newHasMore = responseRecords.length > pageSize
        if (newHasMore !== hasMore) {
          setHasMore(newHasMore)
        }
        if (pagination === 'standard') {
          if (newHasMore) {
            setCurrentRecords(responseRecords.slice(0, -1))
          } else {
            setCurrentRecords(responseRecords)
          }
        } else if (pagination === 'infinite-scroll') {
          setCurrentRecords(currentRecords.concat(responseRecords))
        }
      } else {
        setCurrentRecords(responseRecords)
      }
    }
  }, [JSON.stringify(queryResult), pagination, pageSize, loading])

  const onRowsScrollEnd = useCallback(
    (params) => {
      if (pagination === 'infinite-scroll' && params.virtualRowsCount >= currentPage * pageSize) {
        setCurrentPage((currentPage) => currentPage + 1)
      }
    },
    [pagination, currentPage, pageSize],
  )

  const onPageChange = useCallback((event, newPage) => {
    setCurrentPage(newPage)
  }, [])

  const handlePageSizeChange = useCallback((event: SelectChangeEvent<number>) => {
    setPageSize(event.target.value as number)
    setCurrentPage(1)
  }, [])

  return (
    <Grid container direction="column" style={{ height: '100%' }}>
      <Grid flex="1">
        <DataGridPremium //
          rows={currentRecords}
          hideFooter={true}
          autoHeight={currentRecords.length === 0}
          {...datagridProps}
          ref={undefined}
          loading={loading}
          onRowsScrollEnd={onRowsScrollEnd}
          disableRowSelectionOnClick
        />
      </Grid>
      {pagination === 'standard' && (
        <Grid height="70px" container justifyContent={'flex-end'} alignContent={'center'} style={{ padding: '10px 0px' }}>
          {rowsPerPageOptions && rowsPerPageOptions.length > 0 && (
            <>
              <Typography variant="subtitle2" component="label" style={{ display: 'flex', paddingRight: '10px', alignItems: 'center' }}>
                {labelRowsPerPage}:
              </Typography>
              <Select value={pageSize} onChange={handlePageSizeChange}>
                {rowsPerPageOptions.map((rpp, index) => (
                  <MenuItem key={index} value={rpp}>
                    {rpp}
                  </MenuItem>
                ))}
              </Select>
            </>
          )}
          <Pagination
            page={currentPage}
            onChange={onPageChange}
            count={hasMore ? currentPage + 100 : currentPage}
            showFirstButton={true}
            boundaryCount={0}
            siblingCount={1}
            style={{ display: 'flex', alignItems: 'center' }}
            disabled={loading}
            renderItem={(params) => {
              if (params.page && params.page <= 4 && params.page > currentPage + 1 && params.type === 'page') return null
              if (params.type === 'first' && currentPage < 4) return null
              return <PaginationItem {...params} />
            }}
          />
        </Grid>
      )}
    </Grid>
  )
})

export default GraphQLDataGrid
